/**
 * ENTITY REGISTRY
 * 
 * Single source of truth for admin schemas, endpoints, table columns and actions.
 * 
 * GROUND RULE:
 * Database column names are law.
 * `registerd`, `member_payment_handeld`, and `member_payment_handeld_date`
 * must match the MySQL column spellings in tblFolk exactly.
 */

export const ENTITIES = {
  users: {
    name: 'all-users',
    listAction: 'get-all-users',
    addAction: 'add-users',
    idField: 'id',
    conditionKey: 'email',
    columns: ['firstname', 'lastname', 'email', 'type_of_member'],
    canAdd: true,
    canDelete: true,
    selector: null,
    schema: {
      username: { label: 'Användarnamn', type: 'text' },
      firstname: { label: 'Förnamn', type: 'text' },
      lastname: { label: 'Efternamn', type: 'text' },
      email: { label: 'E-post', type: 'email' },
      tel: { label: 'Tel.', type: 'text' },
      adress: { label: 'Adress', type: 'text' },
      postnummer: { label: 'Postnummer', type: 'number' },
      type_of_member: {
        label: 'Medlemstyp',
        type: 'select',
        optionsSource: 'memberTypeNames'
      },
      registerd: { label: 'Registrerad', type: 'date', readOnly: true }
    }
  },
  prices: {
    name: 'prices',
    listAction: 'get-all-prices',
    addAction: 'add-prices',
    idField: 'id',
    conditionKey: 'type_of_object',
    columns: ['type_of_object', 'member_fee', 'pool_table', 'darts'],
    canAdd: true,
    canDelete: true,
    selector: null,
    schema: {
      type_of_object: { label: 'Medlemstyp', type: 'text' },
      member_fee: { label: 'Medlemsavgift', type: 'number' },
      pool_table: { label: 'Pool bord', type: 'number' },
      darts: { label: 'Darts', type: 'number' }
    }
  },
  objects: {
    name: 'bookable-objects',
    listAction: 'get-all-objects',
    addAction: 'add-objects',
    idField: 'id',
    conditionKey: 'type_of_object',
    columns: ['type_of_object', 'bookable', 'price_group'],
    canAdd: true,
    canDelete: true,
    selector: null,
    schema: {
      type_of_object: { label: 'Namn', type: 'text' },
      bookable: { label: 'Bokningsbar', type: 'select', options: ['Välj', '1', '0'] },
      // Note: price_group still references tblPrices_OLD ids (1 = pool_table, 8 = darts)
      price_group: { label: 'Prisgrupp', type: 'select', options: ['Välj', '1', '8'], optionsSource: 'priceGroupOptions' }
    }
  },
  bookings: {
    name: 'list-bookings',
    listAction: 'get-selected-bookings',
    addAction: null,
    idField: 'id',
    conditionKey: null,
    columns: ['firstname', 'lastname', 'email', 'type_of_object', 'timeId', 'bookingTimestamp'],
    canAdd: false,
    canDelete: false,
    selector: 'date',
    schema: {
      firstname: { label: 'Förnamn', type: 'text' },
      lastname: { label: 'Efternamn', type: 'text' },
      email: { label: 'Email', type: 'text' },
      type_of_object: { label: 'Bord', type: 'text' },
      timeId: { label: 'Tidpunkt', type: 'text' },
      bookingTimestamp: { label: 'Bokat', type: 'text' }
    }
  },
  memberFees: {
    name: 'member-fees',
    // Note: get-all-member-fees only reports types 1-3
    listAction: 'get-all-member-fees',
    addAction: null,
    idField: 'id',
    conditionKey: 'email',
    columns: ['firstname', 'lastname', 'email', 'member_fee', 'total_bookings', 'total_with_fee', 'member_payment_handeld_date'],
    canAdd: false,
    canDelete: false,
    selector: 'month',
    schema: {
      firstname: { label: 'Förnamn', type: 'text', readOnly: true },
      lastname: { label: 'Efternamn', type: 'text', readOnly: true },
      email: { label: 'Email', type: 'text', readOnly: true },
      member_fee: { label: 'Medlemsavgift', type: 'number', readOnly: true },
      total_bookings: { label: 'Bokningsavgifter', type: 'number', readOnly: true },
      total_with_fee: { label: 'Totalt', type: 'number', readOnly: true },
      member_payment_handeld_date: { label: 'Medlemsavgift betalats till', type: 'date' }
    }
  },
  closedBookings: {
    name: 'close-bookings',
    listAction: 'get-all-closed-bookings',
    addAction: 'add-closed',
    idField: 'id',
    conditionKey: 'id',
    columns: ['start_date', 'end_date'],
    canAdd: true,
    canDelete: true,
    selector: null,
    schema: {
      start_date: { label: 'Startdatum', type: 'date' },
      end_date: { label: 'Slutdatum', type: 'date' }
    }
  }
};

export const getEntityByKey = (key) => ENTITIES[key];
export const getEntityByListAction = (action) => Object.values(ENTITIES).find(e => e.listAction === action);
